import { useEffect } from 'react';
import { ConnectGate } from './components/ConnectGate';
import { ProcessDrawer } from './components/ProcessDrawer';
import { Sidebar } from './components/Sidebar';
import { TopBar } from './components/TopBar';
import { Alerts } from './components/views/Alerts';
import { Logs } from './components/views/Logs';
import { Metrics } from './components/views/Metrics';
import { Overview } from './components/views/Overview';
import { Server } from './components/views/Server';
import { useStore } from './state/store';

export function App() {
  const auth = useStore((s) => s.auth);
  const view = useStore((s) => s.view);
  const restore = useStore((s) => s.restore);
  const loadError = useStore((s) => s.loadError);
  const streamStatus = useStore((s) => s.streamStatus);
  const retryStream = useStore((s) => s.retryStream);

  useEffect(() => {
    void restore();
  }, [restore]);

  if (auth === 'unknown' || auth === 'checking') return <Splash />;
  if (auth === 'rejected') return <ConnectGate />;

  return (
    <div
      className="shell"
      style={{
        minHeight: '100vh',
        display: 'flex',
        gap: 18,
        padding: 18,
        background: 'var(--page)',
        color: 'var(--ink)',
        fontSize: 15,
      }}
    >
      <Sidebar />

      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        <TopBar />

        {streamStatus === 'closed' || loadError ? (
          <Banner
            message={
              loadError
                ? `Could not read from the bridge: ${loadError}`
                : 'The live stream is disconnected. Reconnecting automatically.'
            }
            onRetry={retryStream}
          />
        ) : null}

        {view === 'overview' ? <Overview /> : null}
        {view === 'metrics' ? <Metrics /> : null}
        {view === 'logs' ? <Logs /> : null}
        {view === 'alerts' ? <Alerts /> : null}
        {view === 'server' ? <Server /> : null}
      </div>

      <ProcessDrawer />
    </div>
  );
}

function Banner({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div
      role="status"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 18px',
        borderRadius: 14,
        background: 'var(--neg-tint)',
        color: 'var(--neg)',
        border: '1px solid var(--line)',
        fontSize: 13,
        flexWrap: 'wrap',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>{message}</div>
      <button
        type="button"
        onClick={onRetry}
        style={{
          fontWeight: 600,
          textDecoration: 'underline',
          textUnderlineOffset: 3,
        }}
      >
        Retry now
      </button>
    </div>
  );
}

function Splash() {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--page)',
        color: 'var(--faint)',
        fontSize: 13,
        gap: 10,
      }}
    >
      <div
        style={{
          width: 14,
          height: 14,
          borderRadius: 999,
          border: '2px solid var(--line)',
          borderTopColor: 'var(--muted)',
          animation: 'spin 700ms linear infinite',
        }}
      />
      Connecting to the bridge…
    </div>
  );
}
